import { ApiResponse } from '@nf/types/api';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '@/hooks/query';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { useGetSiteConfigs } from '../use-site-configs';
import { ApiBackendInfo } from '../athena/types';
import { CwcJackpotRulesPopupViewedStatus } from './types';
import { useState } from 'react';

/**
 * 取得 CwcJackpot User 是否已看過 Rules 彈窗
 * @param apiBackendInfo 
 * @returns CwcJackpotRulesPopupViewedStatus
 */
const fetchCwcJackpotRulesPopupViewedStatus = async (apiBackendInfo: ApiBackendInfo): Promise<ApiResponse<CwcJackpotRulesPopupViewedStatus>> => {
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/CwcJackpot/GetRulesPopupViewedStatus`)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

/**
 * 記錄 CwcJackpot User 已看過 Rules 彈窗
 * @param apiBackendInfo
 * @returns ApiResponse<void>
 */
const setCwcJackpotRulesPopupViewedStatus = async (apiBackendInfo: ApiBackendInfo): Promise<ApiResponse<void>> => {
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/CwcJackpot/SetRulesPopupViewedStatus`)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

const setViewedStatus = async (queryClient: QueryClient, apiBackendInfo: ApiBackendInfo) => {
	await setCwcJackpotRulesPopupViewedStatus(apiBackendInfo);

	const old = queryClient.getQueryData<ApiResponse<CwcJackpotRulesPopupViewedStatus>>(
		[queryKeys.CWC_JACKPOT_RULES_POPUP_VIEWED_STATUS, apiBackendInfo.url]
	);

	if (old) {
		const newData = {
			...old,
			Data: {
				...old.Data,
				IsViewed: true,
			},
		};
		queryClient.setQueryData(
			[queryKeys.CWC_JACKPOT_RULES_POPUP_VIEWED_STATUS, apiBackendInfo.url],
			newData
		);
	}
};

export const useCwcJackpotRulesPopupViewedStatus = () => {
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const apiBackendInfo = useApiBackendInfo();
	const [hasCheckViewedStatus, setHasCheckViewedStatus] = useState(false);
	const queryClient = useQueryClient();

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [
			queryKeys.CWC_JACKPOT_RULES_POPUP_VIEWED_STATUS,
			apiBackendInfo.url,
			apiBackendInfo.token
		],
		queryFn: () => fetchCwcJackpotRulesPopupViewedStatus(apiBackendInfo),
		staleTime: Infinity,
		cacheTime: Infinity,
		enabled: Boolean(siteConfig?.EnableCwcJackpot) && apiBackendInfo.isReady && !hasCheckViewedStatus,
		onSuccess: (response) => {
			if (response.Data) {
				setHasCheckViewedStatus(true);
			}
		}
	});

	const setViewed = async () => {
		await setViewedStatus(queryClient, apiBackendInfo);
		refetch();
	}

	return {
		data: data?.Data,
		isLoading,
		isError,
		error,
		refetch,
		setViewed
	};
}; 